/**
 * Create Sink Faucet
 *
 * Shared helper for creating a sink faucet object positioned relative to a sink.
 * Used by assembleVanity and bathroom post-processing.
 *
 * Supports two mount types:
 *   - 'counter' (default): faucet sits on countertop behind sink
 *   - 'wall': faucet mounts on wall above countertop
 */

const { INCHES_TO_METERS, VANITY_DIMENSIONS } = require('./constants');
const {
  getWallInwardNormal,
  reSnapToWallPreservingLateralPosition,
} = require('./placementUtils');
const { vesselRimAboveCounter } = require('./calculateSinkSeatY');

/**
 * @param {Object} options
 * @param {Object} options.sinkPos - {x, z} sink center position
 * @param {string} options.sinkId - Sink object ID
 * @param {string} options.sinkType - Sink discriminator ('undermount' | 'vessel' | 'dropin' | 'pedestal' | 'wall-mount'), canonical shape
 * @param {string} [options.countertopId] - Countertop object ID; pass null/empty for standalone sinks (pedestal/wall-mount) where the faucet sits on the sink's own deck
 * @param {number} options.countertopSurfaceY - Y coordinate of countertop top surface
 * @param {Object} [options.countertop] - `{ center: {x,z}, depth }` of the countertop the faucet lands on. Counter mount only: caps the setback so the faucet body cannot extend past the counter's back edge (i.e. into the wall). Omit for standalone sinks (pedestal/wall-hung), whose deck is part of the unit.
 * @param {Object} options.rotation - {x, y, z} rotation
 * @param {Object} [options.faucetModelData] - Faucet model data from unifiedObjects
 * @param {Object} [options.sinkModelData] - Sink model data (for sink-cutout depth)
 * @param {string} options.fixtureGroupId - Fixture group ID
 * @param {string} [options.faucetFinish='chrome'] - Faucet finish
 * @param {string} [options.faucetId] - Explicit faucet ID (auto-generated if omitted)
 * @param {string} [options.label='Faucet'] - Display label
 * @param {string} [options.addedBy='create_bathroom_vanity'] - Source tag
 * @param {'counter'|'wall'} [options.mountType='counter'] - Faucet mount type
 * @param {number} [options.deckOffsetBehindSink] - Counter mount only: distance (m) from the sink centre back to the faucet deck. Overrides the basin-depth-derived default for standalone sinks whose deck is part of the unit (wall-hung).
 * @param {Object} [options.sinkLateralPos] - {x, z} lateral position (wall-mounted only)
 * @param {number} [options.vanityDepth] - Vanity depth in meters (wall-mounted fallback)
 * @param {string[]} [options.parentWallIds] - Parent wall IDs (wall-mounted only)
 * @param {Object[]} [options.walls] - Room walls array (wall-mounted only)
 * @returns {Object} Faucet object ready for Firestore
 */
function createSinkFaucet({
  sinkPos,
  sinkId,
  sinkType,
  countertopId,
  countertopSurfaceY,
  countertop,
  rotation,
  faucetModelData,
  sinkModelData,
  fixtureGroupId,
  faucetFinish = 'chrome',
  faucetId,
  label = 'Faucet',
  addedBy = 'create_bathroom_vanity',
  mountType = 'counter',
  deckOffsetBehindSink,
  sinkLateralPos,
  vanityDepth,
  parentWallIds,
  walls,
}) {
  const sinRy = Math.sin(rotation.y);
  const cosRy = Math.cos(rotation.y);
  const sinkDims = VANITY_DIMENSIONS.sink;

  // Faucet dimensions from model or defaults (~6"x10"x6")
  const faucetScale =
    faucetModelData?.dimensions?.unit === 'inches'
      ? 1
      : faucetModelData?.scaleFactor || 1;
  const faucetDims = faucetModelData?.dimensions
    ? {
        x:
          (faucetModelData.dimensions.width || 6) *
          faucetScale *
          INCHES_TO_METERS,
        y:
          (faucetModelData.dimensions.height || 10) *
          faucetScale *
          INCHES_TO_METERS,
        z:
          (faucetModelData.dimensions.depth || 6) *
          faucetScale *
          INCHES_TO_METERS,
      }
    : { x: 0.15, y: 0.25, z: 0.15 };

  // Detect actual mount type from model data — override if model disagrees with requested mountType
  const modelMountType = faucetModelData?.mountType;
  let effectiveMountType = mountType;
  if (modelMountType === 'wall-mount' && mountType !== 'wall') {
    effectiveMountType = 'wall';
  } else if (
    modelMountType &&
    modelMountType !== 'wall-mount' &&
    mountType === 'wall'
  ) {
    effectiveMountType = 'counter';
  }

  let faucetCenter;
  let faucetParents;
  let wallMounted = false;

  if (effectiveMountType === 'wall') {
    // Wall-mounted faucet: mounting point flush with wall, 5" above countertop
    const WALL_MOUNT_HEIGHT_ABOVE_COUNTER = 5 * INCHES_TO_METERS;

    const lateralPos = sinkLateralPos || sinkPos;
    const parentWall = parentWallIds?.[0]
      ? (walls || []).find((w) => w.id === parentWallIds[0])
      : null;

    // Catalog dimensions are post-rotation (computed after auto-orientation in the
    // Kohler uploader), so depth is already the rendered depth — how far the model
    // extends from the wall.
    const renderedDepth = faucetDims.z;
    let faucetCenterY = countertopSurfaceY + WALL_MOUNT_HEIGHT_ABOVE_COUNTER;

    // A vessel stands ON the counter, and a tall one reaches past that
    // height: the faucet's lowest point stays at least 1" above the bowl's
    // rim (the user, 2026-10-30 — the 5" rule put it half an inch UNDER an
    // 8" Veil vessel's rim).
    const VESSEL_RIM_CLEARANCE = 1 * INCHES_TO_METERS;
    const rimAboveCounter =
      sinkType === 'vessel'
        ? vesselRimAboveCounter({
            modelHeightMeters:
              sinkModelData?.dimensions?.height * INCHES_TO_METERS,
            vesselRingOffsetInches:
              sinkModelData?.['vessel-bottom']?.yOffsetFromBottomInches,
          })
        : null;
    if (rimAboveCounter !== null) {
      faucetCenterY = Math.max(
        faucetCenterY,
        countertopSurfaceY +
          rimAboveCounter +
          VESSEL_RIM_CLEARANCE +
          faucetDims.y / 2
      );
    }

    if (parentWall) {
      const xz = reSnapToWallPreservingLateralPosition(
        parentWall,
        lateralPos,
        renderedDepth
      );
      faucetCenter = { x: xz.x, y: faucetCenterY, z: xz.z };
    } else {
      // Fallback: offset from lateral position along vanity facing direction
      const wallOffset = (vanityDepth || 0.5) / 2 - renderedDepth / 2;
      faucetCenter = {
        x: lateralPos.x - wallOffset * sinRy,
        y: faucetCenterY,
        z: lateralPos.z - wallOffset * cosRy,
      };
    }

    faucetParents = [{ id: sinkId, type: 'sink' }];
    wallMounted = true;
  } else {
    // Counter-mounted faucet: position on countertop behind sink
    const BACK_EDGE_OFFSET = 1 * INCHES_TO_METERS;

    let mountingPointOffset = { x: 0, y: 0, z: -0.0508 };
    if (faucetModelData?.['faucet-mounting-points']) {
      const mountingData = faucetModelData['faucet-mounting-points'];
      const centerPoint = mountingData.points?.find(
        (p) => p.id === 'center' || p.id === 'center-point'
      );
      const modelCenter = mountingData.metadata?.modelCenter;

      if (centerPoint && modelCenter) {
        mountingPointOffset = {
          x: (centerPoint.position.x - modelCenter.x) * INCHES_TO_METERS,
          y: (centerPoint.position.y - modelCenter.y) * INCHES_TO_METERS,
          z: (centerPoint.position.z - modelCenter.z) * INCHES_TO_METERS,
        };
      }
    }

    let sinkDepthForFaucet = sinkDims.depth;
    const isVesselSink = sinkType === 'vessel';

    if (isVesselSink) {
      const modelDepthInches = sinkModelData?.dimensions?.depth;
      if (modelDepthInches) {
        sinkDepthForFaucet = modelDepthInches * INCHES_TO_METERS;
      }
    } else {
      const sinkCutout = sinkModelData?.['sink-cutout'];
      if (sinkCutout?.metadata?.bounds) {
        const bounds = sinkCutout.metadata.bounds;
        sinkDepthForFaucet = (bounds.maxZ - bounds.minZ) * INCHES_TO_METERS;
      }
    }

    const sinkBackEdgeOffset =
      deckOffsetBehindSink ?? sinkDepthForFaucet / 2 + BACK_EDGE_OFFSET;
    let faucetOffsetBehindSink = sinkBackEdgeOffset + mountingPointOffset.z;

    // Keep the faucet body on the countertop. The setback above is derived
    // ENTIRELY from the sink — for the default undermount (15.1" cutout,
    // -2" mounting offset) it puts the faucet's back face 9.9" behind the
    // counter centre, so anything shallower than ~19.7" pushes the faucet
    // through the wall behind it. The counter's back edge is flush with
    // that wall, so containing the body here is the whole fix: measured on
    // dev, 25 of 259 scan-built vanities had a faucet inside the wall.
    if (countertop?.depth > 0) {
      // How far the sink centre already sits behind the counter centre
      // (positive = toward the wall), in the same frame as the offset.
      const sinkBehindCounter = countertop.center
        ? (countertop.center.x - sinkPos.x) * sinRy +
          (countertop.center.z - sinkPos.z) * cosRy
        : 0;
      // Back edge sits `depth/2` behind the counter centre; the sink centre
      // is already `sinkBehindCounter` of that, so the room left behind the
      // SINK is the difference, less the faucet's own half-depth.
      const maxOffsetBehindSink =
        countertop.depth / 2 - sinkBehindCounter - faucetDims.z / 2;
      faucetOffsetBehindSink = Math.min(
        faucetOffsetBehindSink,
        Math.max(0, maxOffsetBehindSink)
      );
    }

    const faucetCenterY = countertopSurfaceY + faucetDims.y / 2;

    faucetCenter = {
      x: sinkPos.x - faucetOffsetBehindSink * sinRy,
      y: faucetCenterY,
      z: sinkPos.z - faucetOffsetBehindSink * cosRy,
    };
    // Standalone sinks (pedestal/wall-mount) have no countertop — the faucet
    // mounts directly on the sink deck. Counter-mounted sinks list both.
    faucetParents = countertopId
      ? [
          { id: countertopId, type: 'countertop' },
          { id: sinkId, type: 'sink' },
        ]
      : [{ id: sinkId, type: 'sink' }];
  }

  const generatedId = faucetId || `faucet_${Date.now()}_${sinkId}`;

  const faucet = {
    id: generatedId,
    type: 'bathroom-sink-faucet',
    label,
    category: { 'bathroom-sink-faucet': true },
    center: faucetCenter,
    dimensions: faucetDims,
    rotation,
    parents: faucetParents,
    fixtureGroupId,
    addedBy,
    lastModified: new Date().toISOString(),
    finish: faucetFinish,
    ...(wallMounted && { wallMounted: true }),
  };

  if (faucetModelData?.url) {
    faucet.modelId = faucetModelData.id;
    faucet.modelUrl = faucetModelData.url;
    faucet.modelName = faucetModelData.name;
    if (faucetModelData.scaleFactor) {
      faucet.scaleFactor = faucetModelData.scaleFactor;
    }
    if (faucetModelData.scale) {
      faucet.scale = faucetModelData.scale;
    }
    if (faucetModelData.centerOffset) {
      faucet.centerOffset = faucetModelData.centerOffset;
    }
    if (faucetModelData['faucet-mounting-points']) {
      faucet['faucet-mounting-points'] =
        faucetModelData['faucet-mounting-points'];
    }
    if (faucetModelData.dimensions) {
      faucet.modelDimensions = faucetModelData.dimensions;
    }
  }

  return faucet;
}

module.exports = { createSinkFaucet };
